import type { CalendarEvent } from "../../@types/sharedSchema";

/**
 * A listing's activity — its meetings, tasks and notes, the realtor's own and her office's — as ONE
 * list, and the one state the listings board shows at a glance.
 *
 * Trello #246 (an office manager's feedback): "it is not easy to see the timeline of a listing — a
 * simpler way to see if someone made an appointment or set a task". The board showed one office icon
 * and a number, and a teammate's TASK did not count at all. Pure: the hooks hand in what they already
 * listen to (CalendarContext's own events, the office's events query, the listing's notes).
 */

export type ListingActivityKind = "meeting" | "task" | "note";

export interface ListingActivityItem {
  key: string;
  kind: ListingActivityKind;
  /** A meeting's or task's start; a note's writing time. */
  at: Date;
  /** A whole-day meeting or task: its time of day means nothing. */
  allDay?: boolean;
  /** The event's title or the note's text (may be empty: a voice note, an untitled task). */
  text: string;
  /** Whose: the event's calendar owner, the note's author. */
  byEmail?: string;
  isMine: boolean;
  /** A task marked done. */
  isDone?: boolean;
  /**
   * Still AHEAD: a meeting that has not ended, or a task not done yet (an overdue task is still to do —
   * it leads the list, oldest first).
   */
  isAhead: boolean;
}

/**
 * The one indicator's look, in order of urgency: an open task whose day has passed; something still
 * ahead (a meeting to come, a task to do); only what already happened.
 */
export type ListingActivityState = "overdue" | "ahead" | "past";

/** What the board says about a listing without opening anything. */
export interface ListingActivitySummary {
  /** Absent: nothing on the listing at all, and no indicator is drawn. */
  state?: ListingActivityState;
  /** What the tooltip names: the overdue task, else the soonest thing ahead, else the latest one that happened. */
  shown?: ListingActivityItem;
  /** How many more things are ahead besides `shown`. */
  moreAhead: number;
}

/** A Firestore Timestamp, a Date, epoch millis or an ISO string — or null when none of them. */
export function activityDate(raw: unknown): Date | null {
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (raw && typeof raw === "object" && typeof (raw as { toDate?: unknown }).toDate === "function") {
    return (raw as { toDate: () => Date }).toDate();
  }
  if (typeof raw === "number" || typeof raw === "string") {
    const date = new Date(raw);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
}

/** The meetings and tasks of one listing as activity. An event with no readable start is left out. */
export function eventActivityItems(
  events: ReadonlyArray<CalendarEvent>,
  myEmail: string | undefined,
  now: Date
): ListingActivityItem[] {
  const me = myEmail?.trim().toLowerCase();
  const items: ListingActivityItem[] = [];
  for (const event of events) {
    const start = activityDate(event.start);
    if (!start) continue;
    const kind: ListingActivityKind = event.eventType === "task" ? "task" : "meeting";
    const end = activityDate(event.end) ?? start;
    const isDone = kind === "task" ? event.status === "done" : undefined;
    const owner = event.userId?.trim().toLowerCase();
    items.push({
      key: event.path ?? event.id ?? `${owner}-${start.getTime()}-${event.title}`,
      kind,
      at: start,
      allDay: !!event.allDay,
      text: event.title?.trim() ?? "",
      byEmail: owner,
      isMine: !!me && owner === me,
      isDone,
      isAhead: kind === "task" ? !isDone : end.getTime() >= now.getTime()
    });
  }
  return items;
}

/** The fields of a note the timeline reads — `useListingNotes`' `DisplayUserNote` has them all. */
export interface ActivityNote {
  id: string;
  note: string;
  noteTime: unknown;
  isMine: boolean;
  authorEmail: string;
  audioUrl?: string;
}

export function noteActivityItems(notes: ReadonlyArray<ActivityNote>): ListingActivityItem[] {
  const items: ListingActivityItem[] = [];
  for (const note of notes) {
    const at = activityDate(note.noteTime);
    if (!at) continue;
    items.push({
      key: `note-${note.authorEmail}-${note.id}`,
      kind: "note",
      at,
      text: note.note?.trim() ?? "",
      byEmail: note.authorEmail?.trim().toLowerCase(),
      isMine: note.isMine,
      isAhead: false
    });
  }
  return items;
}

/**
 * The timeline's order: what is still AHEAD first, soonest first (an overdue task leads); then what
 * already happened, newest first.
 */
export function orderListingActivity(items: ReadonlyArray<ListingActivityItem>): {
  ahead: ListingActivityItem[];
  past: ListingActivityItem[];
} {
  const ahead = items.filter((item) => item.isAhead).sort((a, b) => a.at.getTime() - b.at.getTime());
  const past = items.filter((item) => !item.isAhead).sort((a, b) => b.at.getTime() - a.at.getTime());
  return { ahead, past };
}

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** An open task whose DAY has passed (a task due earlier today is not overdue yet). */
export function isOverdueTask(item: Pick<ListingActivityItem, "kind" | "isAhead" | "at">, now: Date): boolean {
  return item.kind === "task" && item.isAhead && startOfDay(item.at) < startOfDay(now);
}

/**
 * ONE state per listing, not a count per kind: the board's question is "is anything happening here, and
 * is anything late?", and the tooltip and the list say what. Two icons with two counts (all meetings,
 * open tasks) had to be learned before they could be read.
 */
export function summarizeListingActivity(items: ReadonlyArray<ListingActivityItem>, now: Date): ListingActivitySummary {
  const { ahead, past } = orderListingActivity(items);
  const overdue = ahead.find((item) => isOverdueTask(item, now));
  if (overdue) return { state: "overdue", shown: overdue, moreAhead: ahead.length - 1 };
  if (ahead.length) return { state: "ahead", shown: ahead[0], moreAhead: ahead.length - 1 };
  if (past.length) return { state: "past", shown: past[0], moreAhead: 0 };
  return { moreAhead: 0 };
}

/**
 * Which of the OFFICE query's events a listing surface adds to the realtor's own: every teammate's
 * meeting AND TASK on a listing. Her own come from her own calendar's listener, org stamp or not.
 *
 * Tasks are shared with the office by default: every calendar event is stamped with its owner's
 * offices when written (`updateCalendarEventFields`), and the board used to drop a teammate's task
 * here as "a private to-do". The listing window and the server's office-activity feed always showed
 * them; now the board does too (Trello #246, Haim 2026-11-04: "should be default").
 */
export function isTeammateListingEvent(event: Pick<CalendarEvent, "userId" | "listingIds">, myEmail: string | undefined): boolean {
  if (!(event.listingIds ?? []).length) return false;
  const me = myEmail?.trim().toLowerCase();
  return !me || event.userId?.trim().toLowerCase() !== me;
}
